// Package imports:
import React from 'react';
// Service imports:
import { formatDate, formatNumber } from '../../services/utils';
// Type imports:
import { PublicTradeTickData } from '../../types/api/PublicTypes';
import { OpenClosingPrice } from '../../types/api/OpenClosingPrice';

interface IOwnProps {
    tradeId: string;
    symbol: string;
    date: string;
    trade: PublicTradeTickData;
    ohlcData: OpenClosingPrice | null;
    securityType: 'share' | 'bond' | 'derivative' | 'etf' | null;
}

const TradeSummary: React.FC<IOwnProps> = ({
    tradeId,
    symbol,
    date,
    trade,
    ohlcData,
    securityType
}) => {
    const aggressorLabel = trade.aggressive_party === 'B' ? 'Buyer' : trade.aggressive_party === 'S' ? 'Seller' : '—';
    const isCancelled = trade.trade_cancel !== null && trade.trade_cancel !== 'N';
    const outsideSpread = trade.outside_spread === 'Y' ? 'Yes' : trade.outside_spread === 'N' ? 'No' : '—';
    const isNonStandard = trade.update_code === 'non_standard';
    const tradeTypeLabel = trade.update_code === 'standard' ? 'Standard' : trade.update_code === 'non_standard' ? 'Non-Standard' : (trade.update_code ?? '—');

    return (
        <div id='TradeSummary'>
            <div className='SummaryHeader'>
                <h4>Trade Execution Analysis</h4>
            </div>
            <div className='SummaryGrid'>
                <div className='SummaryItem'>
                    <span className='Label'>Trade ID</span>
                    <span className='Value'>{tradeId}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Symbol</span>
                    <span className='Value'>{symbol}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Date</span>
                    <span className='Value'>{date}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Time</span>
                    <span className='Value'>{formatDate(trade.timestamp, true, true)}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Price</span>
                    <span className='Value'>{formatNumber(trade.price)}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Volume</span>
                    <span className='Value'>{formatNumber(trade.volume, 'volume')}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Value</span>
                    <span className='Value'>{formatNumber(trade.value, 'volume')}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Buyer</span>
                    <span className='Value'>{trade.trade_buyer ?? '—'}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Seller</span>
                    <span className='Value'>{trade.trade_seller ?? '—'}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Trade Class</span>
                    <span className='Value'>{trade.trade_class ?? '—'}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Trade Type</span>
                    <span className={`Value${isNonStandard ? ' NonStandard' : ''}`}>{tradeTypeLabel}</span>
                </div>
                {!isNonStandard && (
                    <div className='SummaryItem'>
                        <span className='Label'>Aggressor</span>
                        <span className='Value'>{aggressorLabel}</span>
                    </div>
                )}
                <div className='SummaryItem'>
                    <span className='Label'>Outside Spread</span>
                    <span className='Value'>{outsideSpread}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Cancelled</span>
                    <span className={`Value ${isCancelled ? 'Cancelled' : ''}`}>
                        {isCancelled ? `Yes (${trade.trade_cancel})` : 'No'}
                    </span>
                </div>
                {isCancelled && trade.timestamp_trade_cancel && (
                    <div className='SummaryItem'>
                        <span className='Label'>Cancel Time</span>
                        <span className='Value'>{formatDate(trade.timestamp_trade_cancel, true, true)}</span>
                    </div>
                )}
                {isNonStandard && trade.timestamp_agreement && (
                    <div className='SummaryItem'>
                        <span className='Label'>Agreement Time</span>
                        <span className='Value'>{formatDate(trade.timestamp_agreement, true, true)}</span>
                    </div>
                )}
                {trade.settlement_date && (
                    <div className='SummaryItem'>
                        <span className='Label'>Settlement Date</span>
                        <span className='Value'>{trade.settlement_date}</span>
                    </div>
                )}
            </div>

            {/* Bond-specific fields */}
            {securityType === 'bond' && (
                <div className='BondFields'>
                    <h6>Bond Data</h6>
                    <div className='SummaryGrid'>
                        {trade.yield !== null && (
                            <div className='SummaryItem'>
                                <span className='Label'>Yield</span>
                                <span className='Value'>{formatNumber(trade.yield)}</span>
                            </div>
                        )}
                        {trade.price_dirty !== null && (
                            <div className='SummaryItem'>
                                <span className='Label'>Dirty Price</span>
                                <span className='Value'>{formatNumber(trade.price_dirty)}</span>
                            </div>
                        )}
                        {trade.duration !== null && (
                            <div className='SummaryItem'>
                                <span className='Label'>Duration</span>
                                <span className='Value'>{formatNumber(trade.duration)}</span>
                            </div>
                        )}
                        {trade.price_per_point !== null && (
                            <div className='SummaryItem'>
                                <span className='Label'>Price Per Point</span>
                                <span className='Value'>{formatNumber(trade.price_per_point)}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Day Context */}
            {ohlcData && (
                <div className='AnalyticsSummary'>
                    <div className='AnalyticsItem'>
                        <span className='Label'>Day Open</span>
                        <span className='Value'>{formatNumber(ohlcData.open)}</span>
                    </div>
                    <div className='AnalyticsItem'>
                        <span className='Label'>Day High</span>
                        <span className='Value'>{formatNumber(ohlcData.high)}</span>
                    </div>
                    <div className='AnalyticsItem'>
                        <span className='Label'>Day Low</span>
                        <span className='Value'>{formatNumber(ohlcData.low)}</span>
                    </div>
                    <div className='AnalyticsItem'>
                        <span className='Label'>Day Close</span>
                        <span className='Value'>{formatNumber(ohlcData.close)}</span>
                    </div>
                    <div className='AnalyticsItem'>
                        <span className='Label'>Day Volume</span>
                        <span className='Value'>{formatNumber(ohlcData.volume, 'volume')}</span>
                    </div>
                    <div className='AnalyticsItem'>
                        <span className='Label'>Day Trades</span>
                        <span className='Value'>{ohlcData.number_of_trades}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TradeSummary;
